import React, { useEffect, useState } from 'react';
import { Glasses } from 'lucide-react';
import { schedulePush } from '../../config/preferencesSync';

/**
 * Reader / focus mode (#196): strip a long detail page to just the content —
 * chrome hidden, a comfortable reading measure, larger prose type. The visual
 * work is one `@media screen` CSS layer in index.css keyed on a `reader-mode`
 * class on `<html>`; everything there resolves through the theme variables,
 * so both themes work by construction and the print layer (its sibling) is
 * untouched.
 *
 * The PREFERENCE persists in `localStorage['readerMode']` — like `theme`, it
 * is a reading preference, not part of the document address, so it never
 * rides the URL and a shared link never forces it on the recipient. The
 * CLASS, though, is owned by the detail page that mounts this hook: it is
 * applied only while a detail page is up (and not in its error state), and
 * the effect cleanup removes it, so lists, authoring screens and platform
 * pages always keep their chrome even while the preference stays on.
 *
 * Ways out (a mode that hides the way back is a bug): the toggle itself stays
 * visible in the action row (`aria-pressed` carries its state), the back link
 * stays on the page, and Escape exits the mode.
 */

const KEY = 'readerMode';

export function getStoredReaderMode() {
  try {
    return localStorage.getItem(KEY) === '1';
  } catch (err) {
    return false;
  }
}

function storeReaderMode(on) {
  try {
    if (on) localStorage.setItem(KEY, '1');
    else localStorage.removeItem(KEY);
  } catch (err) {
    /* private mode etc. — the in-page toggle still works */
  }
  // Synced preferences (issue 291): push the change to the account
  // (no-op signed out).
  schedulePush();
}

/**
 * Owns the mode for one mounted detail page: `[active, toggle]`.
 * `suspended` (the page's error state) lifts the class without touching the
 * stored preference — a 404 card renders with full chrome.
 */
export function useReaderMode(suspended = false) {
  const [on, setOn] = useState(getStoredReaderMode);
  const active = on && !suspended;

  useEffect(() => {
    if (!active) return undefined;
    document.documentElement.classList.add('reader-mode');
    const onKey = (e) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        setOn(false);
        storeReaderMode(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.documentElement.classList.remove('reader-mode');
      window.removeEventListener('keydown', onKey);
    };
  }, [active]);

  const toggle = () => {
    setOn((prev) => {
      storeReaderMode(!prev);
      return !prev;
    });
  };

  return [active, toggle];
}

export default function ReaderModeButton({ active, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      aria-label="Reader mode"
      title={active ? 'Exit reader mode (Esc)' : 'Reader mode'}
      className="flex-shrink-0 p-1 mt-1 transition-transform duration-150 hover:scale-110"
    >
      <Glasses
        className="w-5 h-5"
        style={{ color: active ? 'var(--gold)' : 'var(--text-muted)' }}
        aria-hidden="true"
      />
    </button>
  );
}
